/**
 * SI-16: filter panel for the supplier-side Supplier Performance page.
 *
 * A supplier reports on itself, so the customer page's supplier-side filters
 * (division, supplier group/code/site, strategic, commodity, supplier type,
 * countries) have nothing to act on. What is left mirrors the legacy panel a
 * supplier saw: Customer group, Customer site, time period, rating band and the
 * N/A toggle. Options come from the rows already loaded — no extra API calls.
 *
 * Reuses `SupplierPerformanceFilterValues` so the hook keeps ONE filter state:
 * `division` = customer group, `site` = customer site, as on the customer page.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Filter, X, AlertCircle } from 'lucide-react';
import { getLast12MonthsRange } from '../dashboard/dashboardFilterUtils';
import { SupplierPerformanceFilterValues } from './SupplierPerformanceFilters';
import { DEFAULT_FILTERS as DEFAULT_FILTER_VALUES } from '../../pages/supplier-performance/constants';

export interface SupplierSelfPerformanceFilterOptions {
  groups: string[];
  sites: Array<{ id: number; name: string; group: string | null }>;
  grades: string[];
  /** Q8: the supplier's own sites present in the rows (>1 only at GROUP view). */
  supplierSites: Array<{ id: number; name: string }>;
}

/** Shared with the customer-side panel so the two bars stay identical. */
const SECONDARY_ACTION = 'px-4 py-2 border border-border rounded-lg hover:bg-muted text-sm';

interface SupplierSelfPerformanceFiltersProps {
  filters: SupplierPerformanceFilterValues;
  options: SupplierSelfPerformanceFilterOptions;
  onApplyFilters: (filters: SupplierPerformanceFilterValues) => void;
  onRefresh: () => void;
  onExport: () => void;
  /** CR-7297: the page's one Export control — busy, unavailable, and what it writes. */
  exporting?: boolean;
  exportDisabled?: boolean;
  exportTitle?: string;
}

export default function SupplierSelfPerformanceFilters({
  filters,
  options,
  onApplyFilters,
  onRefresh,
  onExport,
  exporting = false,
  exportDisabled = false,
  exportTitle,
}: SupplierSelfPerformanceFiltersProps) {
  const [local, setLocal] = useState<SupplierPerformanceFilterValues>(filters);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { setLocal(filters); }, [filters]);

  // Pickers apply on change (they are client-side); the date inputs apply on
  // Search because they refetch from the server.
  const applyNow = <K extends keyof SupplierPerformanceFilterValues>(key: K, value: SupplierPerformanceFilterValues[K]) => {
    const next = { ...local, [key]: value };
    setLocal(next);
    onApplyFilters(next);
  };

  const last12 = getLast12MonthsRange();
  const isLast12 = local.dateFrom === last12.dateFrom && local.dateTo === last12.dateTo;

  const sitesInGroup = local.division === 'all'
    ? options.sites
    : options.sites.filter((s) => (s.group ?? '') === local.division);

  // Counted from the APPLIED filters, not the pending ones: the count
  // describes the results on screen, while "Not applied yet" describes the
  // edit that has not reached them. Counting `local` here made the bar say
  // "1 filter active" and "Not applied yet" at once, over an unfiltered table.
  const activeCount = [
    filters.supplierSite !== 'all',
    filters.division !== 'all',
    filters.site !== 'all',
    filters.rating !== 'all',
    filters.dateFrom !== '' || filters.dateTo !== '',
    !filters.excludeNARatings,
  ].filter(Boolean).length;

  /**
   * A change is waiting on Apply.
   *
   * Only the date inputs can raise this. Every picker here filters the rows
   * already in the browser (the server returns the whole set for a supplier —
   * see `filteredSuppliers` in useSupplierPerformance), so they apply on
   * change and settle this back to false in the same tick; the dates refetch,
   * so they wait. The prompt therefore appears exactly when something is
   * genuinely unapplied, and never nags about a picker that already took.
   */
  const hasPendingChanges = useMemo(
    () => (Object.keys(local) as Array<keyof SupplierPerformanceFilterValues>)
      .some((key) => local[key] !== filters[key]),
    [local, filters],
  );

  const rangeSummary = isLast12
    ? 'Last 12 Months (Default)'
    : (local.dateFrom && local.dateTo ? `${local.dateFrom} to ${local.dateTo}` : 'all dates');

  const clearFilters = () => {
    setLocal(DEFAULT_FILTER_VALUES);
    onApplyFilters(DEFAULT_FILTER_VALUES);
  };

  return (
    /* CR-7264 rework: same filter design as the customer-side panel and the
       dashboard — a bar that stays put, opened and closed by the Show/Hide
       filters button alone, over sections and a footer. Behaviour is
       deliberately NOT unified: the pickers here stay instant because they
       filter client-side, and only the dates wait for Apply. */
    <div className="mb-4 rounded-lg border bg-card">
      <div className="px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-foreground">
            <Filter className="h-4 w-4 text-muted-foreground" />
            Filters
          </span>
          {activeCount > 0 && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-primary bg-primary/10">
              {activeCount} {activeCount === 1 ? 'filter' : 'filters'} active
            </span>
          )}
          {hasPendingChanges && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-200 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300 transition-colors"
            >
              <AlertCircle className="h-3.5 w-3.5" />
              Changes not applied
            </button>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <button type="button" onClick={onRefresh} className={SECONDARY_ACTION}>
            Refresh
          </button>
          {/* CR-7297: the page's only Export (the toggle-side duplicate is gone). */}
          <button
            type="button"
            onClick={onExport}
            disabled={exporting || exportDisabled}
            aria-busy={exporting}
            title={exportTitle}
            className={`${SECONDARY_ACTION} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {exporting ? 'Exporting...' : 'Export to Excel'}
          </button>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className={`px-4 py-2 border rounded-lg text-sm ${
              expanded
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border hover:bg-muted'
            }`}
          >
            {expanded ? 'Hide filters' : 'Show filters'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t">
          <div className="px-6 py-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
          {options.supplierSites.length > 1 && (
            <div className="space-y-1.5">
              <Label htmlFor="self-supplier-site" className="text-xs">Supplier Site</Label>
              <Select value={local.supplierSite} onValueChange={(value) => applyNow('supplierSite', value)}>
                <SelectTrigger id="self-supplier-site" className="h-9"><SelectValue placeholder="All my sites" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All my sites</SelectItem>
                  {options.supplierSites.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="self-customer-group" className="text-xs">Customer Group</Label>
            <Select
              value={local.division}
              onValueChange={(value) => {
                // Changing group drops a site that is no longer in it.
                const next = { ...local, division: value, site: 'all' };
                setLocal(next);
                onApplyFilters(next);
              }}
            >
              <SelectTrigger id="self-customer-group" className="h-9"><SelectValue placeholder="All Groups" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Groups</SelectItem>
                {options.groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="self-customer-site" className="text-xs">Customer Site</Label>
            <Select value={local.site} onValueChange={(value) => applyNow('site', value)}>
              <SelectTrigger id="self-customer-site" className="h-9"><SelectValue placeholder="All Sites" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sites</SelectItem>
                {sitesInGroup.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="self-date-from" className="text-xs">From</Label>
            <Input
              id="self-date-from"
              type="date"
              className="h-9"
              value={local.dateFrom}
              onChange={(e) => setLocal({ ...local, dateFrom: e.target.value, timePeriod: 'custom' })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="self-date-to" className="text-xs">To</Label>
            <Input
              id="self-date-to"
              type="date"
              className="h-9"
              value={local.dateTo}
              onChange={(e) => setLocal({ ...local, dateTo: e.target.value, timePeriod: 'custom' })}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="self-rating" className="text-xs">Rating</Label>
            <Select
              value={local.rating}
              onValueChange={(value) => {
                const next = value === 'N/A'
                  ? { ...local, rating: value, excludeNARatings: false }
                  : { ...local, rating: value };
                setLocal(next);
                onApplyFilters(next);
              }}
            >
              <SelectTrigger id="self-rating" className="h-9"><SelectValue placeholder="All Ratings" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ratings</SelectItem>
                {options.grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                <SelectItem value="N/A">N/A (No Data)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* A quick range, so it applies on click — as the presets do on the
              dashboard and on the customer-side panel. */}
          <div className="flex items-center gap-2 h-9">
            <Button
              size="sm"
              variant={isLast12 ? 'default' : 'outline'}
              className="h-9"
              onClick={() => {
                const next = { ...local, ...last12, timePeriod: 'last12Months' };
                setLocal(next);
                onApplyFilters(next);
              }}
            >
              Last 12 months
            </Button>
          </div>
        </div>

            <div className="flex items-center gap-2 mt-4">
              <Checkbox
                id="self-exclude-na"
                checked={local.excludeNARatings}
                onCheckedChange={(checked) => applyNow('excludeNARatings', checked === true)}
              />
              <Label htmlFor="self-exclude-na" className="text-sm cursor-pointer">Exclude N/A Ratings</Label>
            </div>
          </div>

          {/* Footer: the two decisions side by side, status alongside — the
              same arrangement as the customer-side panel. */}
          <div className="border-t px-6 py-4">
            <div className="flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={clearFilters}
                className="px-5 py-2.5 border border-border rounded-md hover:bg-muted flex items-center gap-2 font-medium transition-all"
              >
                <X className="h-4 w-4" />
                Clear all
              </button>

              <button
                type="button"
                onClick={() => onApplyFilters(local)}
                className={`px-6 py-2.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 font-medium shadow-sm transition-all ${
                  hasPendingChanges ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-background' : ''
                }`}
              >
                Apply filters
              </button>

              {hasPendingChanges ? (
                <p className="inline-flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
                  <AlertCircle className="h-4 w-4" />
                  Your selection isn't in the results yet — click Apply filters
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">Showing results for {rangeSummary}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
